import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Toast as ToastPrimitive } from '@base-ui/react/toast';
import { Button } from 'components/banff-ui/Button';
import { cn } from 'components/banff-ui/cn';
import { Spinner } from 'components/banff-ui/Spinner';
import CircleCheck from 'lucide-react/dist/esm/icons/circle-check';
import Info from 'lucide-react/dist/esm/icons/info';
import OctagonX from 'lucide-react/dist/esm/icons/octagon-x';
import TriangleAlert from 'lucide-react/dist/esm/icons/triangle-alert';
import X from 'lucide-react/dist/esm/icons/x';

const toast = ToastPrimitive.createToastManager();

/** Hosts toast notifications. Mount once near the app root inside `.banff-ui`. */
export function ToastProvider(props: ToastPrimitive.Provider.Props) {
  return <ToastPrimitive.Provider data-slot="toast-provider" {...props} />;
}

export function ToastPortal(props: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />;
}

/** Fixed region where toasts stack. */
export function ToastViewport({
  className,
  ...props
}: ToastPrimitive.Viewport.Props) {
  return (
    <ToastPrimitive.Viewport
      data-slot="toast-viewport"
      {...props}
      className={cn(
        'pointer-events-none fixed inset-x-4 bottom-4 z-[120] mx-auto w-auto max-w-sm outline-none sm:left-auto sm:right-4 sm:mx-0 sm:w-full',
        className,
      )}
    />
  );
}

export function ToastRoot({ className, ...props }: ToastPrimitive.Root.Props) {
  return (
    <ToastPrimitive.Root
      data-slot="toast"
      {...props}
      className={cn(
        'group/toast bg-popover text-popover-foreground border-border pointer-events-auto absolute bottom-0 right-0 w-full origin-bottom select-none rounded-lg border shadow-lg outline-none will-change-transform',
        'focus-visible:ring-ring/50 z-[calc(1000-var(--toast-index))] focus-visible:ring-2',
        '[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
        'h-[var(--height)] [transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_500ms,height_150ms]',
        "after:absolute after:left-0 after:top-full after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
        'data-[expanded]:h-[var(--toast-height)] data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]',
        'data-[limited]:opacity-0 data-[starting-style]:[transform:translateY(150%)]',
        '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]',
        'data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        className,
      )}
    />
  );
}

export function ToastContent({
  className,
  ...props
}: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      {...props}
      className={cn(
        'duration-250 flex h-full items-center gap-3 overflow-hidden p-4 transition-opacity ease-[cubic-bezier(0.22,1,0.36,1)] data-[behind]:opacity-0 data-[expanded]:opacity-100',
        className,
      )}
    />
  );
}

export function ToastTitle({
  className,
  ...props
}: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      {...props}
      className={cn('text-sm font-medium', className)}
    />
  );
}

export function ToastDescription({
  className,
  ...props
}: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      {...props}
      className={cn('text-muted-foreground text-sm', className)}
    />
  );
}

export function ToastAction({
  className,
  render = <Button size="sm" variant="outline" />,
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      {...props}
      render={render}
      className={cn('shrink-0', className)}
    />
  );
}

export function ToastClose({
  children,
  className,
  render = <Button size="icon-sm" variant="ghost" />,
  ...props
}: ToastPrimitive.Close.Props) {
  return (
    <ToastPrimitive.Close
      aria-label="Close toast"
      data-slot="toast-close"
      {...props}
      render={render}
      className={cn(
        'text-muted-foreground hover:text-foreground relative shrink-0 after:absolute after:-inset-2 after:content-[""]',
        className,
      )}
    >
      {children ?? <X aria-hidden />}
    </ToastPrimitive.Close>
  );
}

function ToastStatusIcon({ type }: { type: string | undefined }) {
  let icon: ReactNode = null;

  if (type === 'success') icon = <CircleCheck aria-hidden />;
  if (type === 'info') icon = <Info aria-hidden />;
  if (type === 'warning') icon = <TriangleAlert aria-hidden />;
  if (type === 'error')
    icon = <OctagonX aria-hidden className="text-destructive" />;
  if (type === 'loading') icon = <Spinner aria-hidden />;

  if (!icon) return null;

  return (
    <span
      data-slot="toast-icon"
      className="shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none"
    >
      {icon}
    </span>
  );
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager();

  return toasts.map((toastItem) => (
    <ToastRoot key={toastItem.id} toast={toastItem}>
      <ToastContent>
        <ToastStatusIcon type={toastItem.type} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <ToastTitle />
          <ToastDescription />
        </div>
        {toastItem.actionProps ? <ToastAction /> : null}
        <ToastClose />
      </ToastContent>
    </ToastRoot>
  ));
}

/**
 * Toast stack and provider. Mount once at the app root.
 * Call `toast.add`, `toast.close`, and `toast.promise` from `components/banff-ui/Toast`.
 */
export function Toaster({
  children,
  toastManager = toast,
  ...props
}: ToastPrimitive.Provider.Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  useLayoutEffect(() => {
    setPortalContainer(hostRef.current);
  }, []);

  return (
    <div className="banff-ui" ref={hostRef}>
      <ToastProvider toastManager={toastManager} {...props}>
        {children}
        <ToastPortal container={portalContainer}>
          <ToastViewport>
            <ToastList />
          </ToastViewport>
        </ToastPortal>
      </ToastProvider>
    </div>
  );
}

export const createToastManager = ToastPrimitive.createToastManager;

/** Default toast manager for the app. Use with the mounted `Toaster`. */
export { toast };
